import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";

// 3rd parties
import { Button, ButtonGroup, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import styled from "styled-components";

// owner
import SntSelect from "@wrappers/ReactSelect/SntSelect";
import organizationClient from "@apis/organizationClient";
import globalSearchClient from "@apis/globalSearchClient";
import useUser from "@hooks/useUser";
import useErrorDialog from "@/cleanup/widgets/dialog/useErrorDialog";

import { PrimaryTag, GreyTag } from "@cleanup/components/TagMore";
import SntOrgBrandIcon from "@widgets/images/SntOrgBrandIcon";
import SntOrgIcon from "@icons/SntOrgIcon";
import SntPartnersIcon from "@icons/SntPartnersIcon";
import SntGlobeIcon from "@icons/SntGlobeIcon";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import SntSystemDevIcon from "@cleanup/components/Icons/SntSystemDevIcon";
import HeaderIconButton from "@/containers/AdminLayout/Header/HeaderIconButton";
import { userSelector } from "@redux/selectors";
import { useCustomBranding } from "@/pages/CustomBranding/useCustomBranding";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import { hasPermission } from "@/hooks/usePermissions";
import { hasFeature } from "@/hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import type { LoginInfo } from "@/types/LoginInfo";
import type { SearchHistoryItem } from "@/containers/AdminLayout/SearchPanel/types";

const PAGE_SIZE = 50;

// --- Shared styled components ---

interface StyleCustomInfoBtnProps {
  $hoverColor?: string;
}

const StyleCustomInfoBtn = styled.div<StyleCustomInfoBtnProps>`
  button:hover {
    background-color: ${(props) => props.$hoverColor || ""};
  }
`;

// --- Pill variant styled components ---

const PillChevron = styled.svg<{ $open: boolean }>`
  width: 12px;
  height: 12px;
  transition: transform 0.15s ease;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0)")};
`;

const PillPanel = styled.div`
  position: fixed;
  z-index: 1060;
  width: 480px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 120px);
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
`;

const PillTabBar = styled.div`
  display: flex;
  border-bottom: 1px solid #e4e7ec;
`;

const PillTab = styled.button<{ $active: boolean }>`
  padding: 10px 16px;
  border: none;
  background: none;
  font-size: inherit;
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  color: inherit;
  cursor: pointer;
  border-bottom: 2px solid ${(props) => (props.$active ? "#1570ef" : "transparent")};
`;

const PillSearch = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e4e7ec;
  font-size: inherit;
  outline: none;
`;

const PillList = styled.div`
  max-height: min(480px, calc(100vh - 220px));
  overflow-y: auto;
`;

const PillItem = styled.div<{ $selected: boolean; $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background: ${(props) =>
    props.$highlighted
      ? props.$selected
        ? "#dbe4ff"
        : "#f0f4f8"
      : props.$selected
        ? "#eff4ff"
        : "transparent"};

  &:hover {
    background: ${(props) => (props.$selected ? "#dbe4ff" : "#f9fafb")};
  }
`;

const PillItemSub = styled.div`
  font-size: 11px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PillSectionLabel = styled.div`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.5;
  letter-spacing: 0.03em;
`;

const PillRecentSection = styled.div`
  background: #f0f4f8;
`;

const PillDivider = styled.div`
  border-top: 1px solid #e4e7ec;
  margin: 4px 0;
`;

// --- Shared helpers ---

interface OrganizationOption {
  label: string;
  value: number;
  type: string;
  commercialPartner?: boolean;
  partnerName?: string;
  partnerVariant?: "CHANNEL_PARTNER" | "MULTI_ORG";
  partnerType?: string;
  orgCount?: number;
  webDomain?: string;
}

interface OrganizationBasicInfo {
  id: number;
  name: string;
  partnerName?: string;
  organisationType: string;
  commercialPartner?: boolean;
  partnerVariant?: "CHANNEL_PARTNER" | "MULTI_ORG";
  partnerType?: string;
  webDomain?: string;
}

interface Section {
  id: string;
  title: string;
  isShow: boolean;
  icon: React.ReactNode;
}

interface OrgSelectorChangeData {
  value: number;
}

// Brand icon for an org — uses Brandfetch when a webDomain is set, falls back
// to initials. Keeps row identity recognisable at a glance.
const OrgIconDecorator = ({
  option,
  size = 20,
}: {
  option: OrganizationOption;
  size?: number;
}) => (
  <SntOrgBrandIcon domain={option.webDomain} name={option.label} size={size} />
);

// Channel partners show their SF partner type (VAR / MSP / INTEGRATOR / …);
// multi-org partners show a "Multi-org" badge. The old single-letter
// 'C' / 'M' badge was opaque to most colleagues.
// `compact` renders the variant inline as "(label)" text instead of a coloured
// badge, so it sits naturally inside the partner-name sub-text in Org-tab
// dropdown rows (STIC-15255).
const PartnerTypeBadge = ({
  option,
  compact = false,
}: {
  option: OrganizationOption;
  compact?: boolean;
}) => {
  let label: string | null = null;
  if (option.partnerVariant === "CHANNEL_PARTNER") {
    label = option.partnerType ?? "";
  } else if (option.partnerVariant === "MULTI_ORG") {
    label = translate(LanguageKey.multi_org_key);
  }
  if (!label) return null;

  if (compact) {
    return <span title={label}>({label})</span>;
  }

  if (option.partnerVariant === "CHANNEL_PARTNER") {
    return <PrimaryTag tagName={label} size="sm" title={label} />;
  }
  return <GreyTag tagName={label} size="sm" title={label} />;
};

// --- Component ---

interface SntOrgMenuSelectorContainerProps {
  value: number | null;
  onChange?: (data: OrgSelectorChangeData) => void;
  disabled?: boolean;
  variant?: "classic" | "pill";
  hideImpersonate?: boolean;
}

const SntOrgMenuSelectorContainer = ({
  value,
  onChange,
  disabled = false,
  variant = "classic",
  hideImpersonate = false,
}: SntOrgMenuSelectorContainerProps) => {
  const loginInfo = useSelector(userSelector) as LoginInfo;
  const { onImpersonateUser, IMPERSONATE_TYPE } = useUser();
  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({});
  const { mergeDataWithValueDefault } = useCustomBranding();

  const customStyle = useMemo(() => {
    let custumInfo = {};
    if (loginInfo && loginInfo.custumInfo) {
      custumInfo = loginInfo.custumInfo;
    }
    return mergeDataWithValueDefault(custumInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo]);

  const [sections, setSections] = useState<Section[]>([]);
  const [mode, setMode] = useState<string>();

  // Classic variant state
  const [allItems, setAllItems] = useState<OrganizationOption[]>([]);
  const [visibleItems, setVisibleItems] = useState<OrganizationOption[]>([]);
  const searchTextRef = useRef("");

  const [orgList, setOrgList] = useState<OrganizationOption[]>([]);
  const [partnerList, setPartnerList] = useState<OrganizationOption[]>([]);
  const [systemOrg, setSystemOrg] = useState<OrganizationOption | null>(null);
  const orgSelectedRef = useRef<number | undefined>(undefined);
  const partnerSelectedRef = useRef<number | undefined>(undefined);

  const [isLoadingData, setLoadingData] = useState(true);
  const [isFirstLoaded, setFirstLoaded] = useState(true);

  // Pill variant state
  const [pillOpen, setPillOpen] = useState(false);
  const [pillPos, setPillPos] = useState({ top: 0, left: 0 });
  const pillTriggerRef = useRef<HTMLButtonElement>(null);
  const pillPopoverRef = useRef<HTMLDivElement>(null);
  const pillSearchRef = useRef<HTMLInputElement>(null);
  const [pillTab, setPillTab] = useState<"PARTNER" | "ORG" | "ALL">("PARTNER");
  const [pillSearch, setPillSearch] = useState("");
  const [pillHighlightIdx, setPillHighlightIdx] = useState(-1);
  const pillListRef = useRef<HTMLDivElement>(null);
  const [recentOrgIds, setRecentOrgIds] = useState<number[]>([]);
  const [recentPartnerIds, setRecentPartnerIds] = useState<number[]>([]);

  // --- Shared impersonate handler ---

  const canImpersonate =
    mode !== "ALL" &&
    (hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY) ||
      hasPermission(UserPermission.ORG_SUPPORT_LOGIN_ADMIN)) &&
    hasFeature(FeatureKey.impersonate);

  const impersonateTarget = useMemo(() => {
    if (!value) return null;
    return [...orgList, ...partnerList].find((o) => o.value === value) || null;
  }, [value, orgList, partnerList]);

  const handleImpersonate = useCallback(() => {
    if (value === null) return;
    let impersonateMode = IMPERSONATE_TYPE.VIEWER;
    if (!hasPermission(UserPermission.ORG_SUPPORT_LOGIN_VIEW_ONLY)) {
      impersonateMode = IMPERSONATE_TYPE.ADMIN;
    }
    onImpersonateUser({
      id: value,
      type: impersonateMode,
      onBadRequest: () => {
        openErrorDialog(translate(LanguageKey.impersonation_insufficient_rights_key));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const renderImpersonateButton = () => {
    if (!canImpersonate) return null;
    const isTargetSystem = impersonateTarget?.type === "SYSTEM";
    const title = isTargetSystem
      ? translate(LanguageKey.support_login_on_system_account_is_not_permitted_key)
      : translate(LanguageKey.support_login_key);
    const icon = <SntSupportUserIcon color="rgb(255, 168, 88)" style={{ opacity: isTargetSystem ? 0.4 : 1 }} />;
    const handleClick = isTargetSystem ? () => {} : handleImpersonate;

    if (variant === "pill") {
      return (
        <HeaderIconButton title={title} onClick={handleClick}>
          {icon}
        </HeaderIconButton>
      );
    }

    return (
      <Button
        variant="sensolus-greylight"
        disabled={isTargetSystem}
        className="ms-1"
        style={{
          color: customStyle.adminSideBarSectionFontColor,
          borderColor: customStyle.adminSideBarSectionFontColor,
        }}
        title={isTargetSystem ? title : undefined}
        onClick={handleClick}
      >
        {icon}
      </Button>
    );
  };

  // --- Close pill popover on outside click ---

  useEffect(() => {
    if (variant !== "pill" || !pillOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        pillTriggerRef.current &&
        !pillTriggerRef.current.contains(target) &&
        (!pillPopoverRef.current || !pillPopoverRef.current.contains(target))
      ) {
        setPillOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [variant, pillOpen]);

  // Fetch recently viewed orgs/partners when pill opens
  useEffect(() => {
    if (variant !== "pill" || !pillOpen) return;
    globalSearchClient
      .getSearchHistory()
      .then(({ data }: { data: SearchHistoryItem[] }) => {
        const orgIds: number[] = [];
        const partnerIds: number[] = [];
        for (const item of data) {
          const cat = item.searchIndex.category;
          const id = Number(item.searchIndex.entityId);
          if (cat === "Organization") {
            // Orgs may also appear as partners in the pill
            if (partnerList.some((p) => p.value === id)) {
              partnerIds.push(id);
            } else {
              orgIds.push(id);
            }
          } else if (cat === "Partner") {
            partnerIds.push(id);
          }
        }
        setRecentOrgIds(orgIds);
        setRecentPartnerIds(partnerIds);
      })
      .catch(() => {
        // silently ignore - recents are non-critical
      });
  }, [variant, pillOpen, partnerList]);

  // Focus pill search when popover opens or tab changes
  useEffect(() => {
    if (pillOpen && pillTab !== "ALL") {
      setTimeout(() => pillSearchRef.current?.focus(), 50);
    }
  }, [pillOpen, pillTab]);

  const pillFilteredItems = useMemo(() => {
    if (variant !== "pill") return [];
    const source = pillTab === "PARTNER" ? partnerList : orgList;
    if (!pillSearch) return source.slice(0, PAGE_SIZE);
    const lower = pillSearch.toLowerCase();
    return source
      .filter((item) => item.label.toLowerCase().includes(lower))
      .slice(0, PAGE_SIZE);
  }, [variant, pillTab, partnerList, orgList, pillSearch]);

  const pillRecentItems = useMemo(() => {
    if (variant !== "pill" || pillSearch) return [];
    const recentIds = pillTab === "PARTNER" ? recentPartnerIds : recentOrgIds;
    if (recentIds.length === 0) return [];
    const source = pillTab === "PARTNER" ? partnerList : orgList;
    const items: OrganizationOption[] = [];
    for (const id of recentIds) {
      if (id === value) continue; // skip the currently selected org — already shown in the pill
      const found = source.find((o) => o.value === id);
      if (found) items.push(found);
    }
    return items.slice(0, 5);
  }, [variant, pillTab, pillSearch, recentPartnerIds, recentOrgIds, partnerList, orgList, value]);

  // Combined list of all navigable items for keyboard navigation (recents + filtered)
  const pillAllNavigableItems = useMemo(() => {
    return [...pillRecentItems, ...pillFilteredItems];
  }, [pillRecentItems, pillFilteredItems]);

  // Reset highlight when search text or tab changes
  useEffect(() => {
    setPillHighlightIdx(-1);
  }, [pillSearch, pillTab]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (pillHighlightIdx < 0 || !pillListRef.current) return;
    const items = pillListRef.current.querySelectorAll("[data-pill-item]");
    items[pillHighlightIdx]?.scrollIntoView({ block: "nearest" });
  }, [pillHighlightIdx]);

  const handlePillSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const itemCount = pillAllNavigableItems.length;
    if (itemCount === 0) return;

    if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      setPillHighlightIdx((prev) => (prev < itemCount - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setPillHighlightIdx((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (pillHighlightIdx >= 0 && pillHighlightIdx < itemCount) {
        handlePillItemClick(pillAllNavigableItems[pillHighlightIdx]);
      } else if (itemCount === 1) {
        handlePillItemClick(pillAllNavigableItems[0]);
      }
    } else if (e.key === "Escape") {
      setPillOpen(false);
    }
  };

  // --- Sync mode from value ---

  useEffect(() => {
    if (isLoadingData) return;
    if (!value) {
      setMode("ALL");
    } else if (systemOrg && systemOrg.value === value) {
      setMode("SYSTEM");
    } else {
      const find = orgList.find((data) => data.value === value);
      if (find) {
        orgSelectedRef.current = value;
        setMode("ORG");
        searchTextRef.current = "";
        setAllItems(orgList);
      } else {
        const partnerFind = partnerList.find((data) => data.value === value);
        if (partnerFind) {
          partnerSelectedRef.current = value;
          setMode("PARTNER");
          searchTextRef.current = "";
          setAllItems(partnerList);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isLoadingData]);

  // --- Load org data ---

  useEffect(() => {
    let existOrg: OrganizationOption | undefined = undefined;
    if (value) {
      existOrg = orgList.find((data) => data.value === value);
      if (!existOrg) {
        existOrg = partnerList.find((data) => data.value === value);
      }
      if (!existOrg && systemOrg?.value === value) {
        existOrg = systemOrg;
      }
      if (!existOrg) {
        setLoadingData(true);
        getOrgList();
      }
    } else if (isFirstLoaded) {
      getOrgList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const getOrgList = () => {
    organizationClient
      .getBasicInfo()
      .then(({ data }: { data: OrganizationBasicInfo[] }) => {
        const listOrg: OrganizationOption[] = [];
        const listPartner: OrganizationOption[] = [];
        let sysOrg: OrganizationOption | null = null;
        data.forEach((item) => {
          const option: OrganizationOption = {
            label: item.name,
            value: item.id,
            type: item.organisationType,
            commercialPartner: item.commercialPartner,
            partnerName: item.partnerName,
            partnerVariant: item.partnerVariant,
            partnerType: item.partnerType,
            webDomain: item.webDomain,
          };
          if (option.type === "SYSTEM") {
            sysOrg = option;
          } else if (option.type === "PARTNER") {
            option.label = item.partnerName || item.name;
            listPartner.push(option);
          } else {
            listOrg.push(option);
          }
        });

        const orgCountByPartner: Record<string, number> = {};
        listOrg.forEach((org) => {
          if (org.partnerName) {
            orgCountByPartner[org.partnerName] = (orgCountByPartner[org.partnerName] || 0) + 1;
          }
        });
        listPartner.forEach((partner) => {
          partner.orgCount = orgCountByPartner[partner.label] || 0;
        });

        setOrgList(listOrg);
        setPartnerList(listPartner);
        setSystemOrg(sysOrg);
        const sectionItems: Section[] = [
          { id: "ORG", title: translate(LanguageKey.org_key), isShow: listOrg.length !== 0, icon: <SntOrgIcon /> },
          { id: "PARTNER", title: translate(LanguageKey.part_key), isShow: listPartner.length !== 0, icon: <SntPartnersIcon /> },
          { id: "ALL", title: translate(LanguageKey.all_key), isShow: true, icon: <SntGlobeIcon /> },
          { id: "SYSTEM", title: translate(LanguageKey.system_key), isShow: sysOrg !== null, icon: <SntSystemDevIcon /> },
        ];
        if (loginInfo.organisation?.organisationType === "SYSTEM") {
          sectionItems.reverse();
        }
        setSections(sectionItems);
        setLoadingData(false);
        setFirstLoaded(false);
      })
      .catch(() => {
        setLoadingData(false);
        setFirstLoaded(false);
      });
  };

  // --- Classic variant helpers ---

  const getFilteredItems = useCallback(
    (sourceItems: OrganizationOption[], searchText: string) => {
      if (!searchText) return sourceItems;
      const lower = searchText.toLowerCase();
      return sourceItems.filter((item) => item.label.toLowerCase().includes(lower));
    },
    []
  );

  useEffect(() => {
    const filtered = getFilteredItems(allItems, searchTextRef.current);
    setVisibleItems(filtered.slice(0, PAGE_SIZE));
  }, [allItems, getFilteredItems]);

  const onMenuScrollToBottom = useCallback(() => {
    const filtered = getFilteredItems(allItems, searchTextRef.current);
    if (visibleItems.length < filtered.length) {
      setVisibleItems(filtered.slice(0, visibleItems.length + PAGE_SIZE));
    }
  }, [allItems, visibleItems.length, getFilteredItems]);

  const onInputChange = useCallback(
    (text: string) => {
      searchTextRef.current = text;
      const filtered = getFilteredItems(allItems, text);
      setVisibleItems(filtered.slice(0, PAGE_SIZE));
    },
    [allItems, getFilteredItems]
  );

  const selectChanged = (item: OrganizationOption) => {
    onChange?.({ value: item.value });
  };

  // --- Pill variant helpers ---

  const pillLabel = useMemo(() => {
    if (mode === "ALL") return translate(LanguageKey.all_key);
    if (mode === "SYSTEM") return translate(LanguageKey.system_key);
    if (mode === "PARTNER") {
      const found = partnerList.find((d) => d.value === value);
      return found?.label || translate(LanguageKey.all_key);
    }
    const found = orgList.find((d) => d.value === value);
    return found?.label || translate(LanguageKey.all_key);
  }, [mode, value, partnerList, orgList]);

  const pillPartnerContext = useMemo(() => {
    if (mode !== "ORG") return null;
    const found = orgList.find((d) => d.value === value);
    if (!found?.partnerName) return null;
    return found;
  }, [mode, value, orgList]);

  const pillIcon = useMemo(() => {
    if (mode === "ALL") return <SntGlobeIcon />;
    if (mode === "SYSTEM") return <SntSystemDevIcon />;
    if (mode === "PARTNER") return <SntPartnersIcon />;
    return <SntOrgIcon />;
  }, [mode]);

  const handlePillTriggerClick = () => {
    if (disabled) return;
    if (!pillOpen && pillTriggerRef.current) {
      const rect = pillTriggerRef.current.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 488);
      setPillPos({ top: rect.bottom + 4, left: Math.max(8, left) });
      setPillTab(mode === "PARTNER" ? "PARTNER" : mode === "ORG" ? "ORG" : partnerList.length > 0 ? "PARTNER" : "ORG");
      setPillSearch("");
    }
    setPillOpen((prev) => !prev);
  };

  const handlePillTabClick = (tab: "PARTNER" | "ORG" | "ALL" | "SYSTEM") => {
    setPillSearch("");
    if (tab === "ALL") {
      setPillOpen(false);
      onChange?.({ value: 0 });
    } else if (tab === "SYSTEM" && systemOrg) {
      setPillOpen(false);
      onChange?.({ value: systemOrg.value });
    } else {
      setPillTab(tab as "PARTNER" | "ORG");
    }
  };

  const handlePillItemClick = (item: OrganizationOption) => {
    setPillOpen(false);
    setPillSearch("");
    onChange?.({ value: item.value });
  };

  // --- Pill trigger label ---

  const selectedOption = useMemo(() => {
    if (value === null || value === 0) return null;
    return (
      orgList.find((d) => d.value === value) ||
      partnerList.find((d) => d.value === value) ||
      (systemOrg && systemOrg.value === value ? systemOrg : null)
    );
  }, [value, orgList, partnerList, systemOrg]);

  const renderPillLabel = () => {
    const orgIcon = selectedOption ? (
      <span className="me-1 d-inline-flex align-items-center">
        <OrgIconDecorator option={selectedOption} size={28} />
      </span>
    ) : null;
    if (pillPartnerContext) {
      return (
        <>
          {pillPartnerContext.partnerName}{" "}
          <PartnerTypeBadge option={pillPartnerContext} />
          {" › "}{orgIcon}{pillLabel}
        </>
      );
    }
    if (mode === "PARTNER") {
      const found = partnerList.find((d) => d.value === value);
      return (
        <>
          {pillLabel}
          {found && <>{" "}<PartnerTypeBadge option={found} /></>}
        </>
      );
    }
    return (
      <>
        {orgIcon}
        {pillLabel}
      </>
    );
  };

  // --- Pill popover content ---

  const renderPillItem = (item: OrganizationOption, navIndex: number, keyPrefix = "") => {
    const isSelected = item.value === value;
    const isHighlighted = navIndex === pillHighlightIdx;
    return (
      <PillItem
        key={`${keyPrefix}${item.value}`}
        data-pill-item
        $selected={isSelected}
        $highlighted={isHighlighted}
        onClick={() => handlePillItemClick(item)}
        onMouseEnter={() => setPillHighlightIdx(navIndex)}
      >
        <div className="flex-shrink-0 me-2 d-flex align-items-center">
          <OrgIconDecorator option={item} size={32} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2">
            <span className={`text-truncate ${isSelected ? "fw-bold" : ""}`}>
              {item.label}
            </span>
            {pillTab === "PARTNER" && (
              <div className="flex-shrink-0"><PartnerTypeBadge option={item} /></div>
            )}
          </div>
          {pillTab === "PARTNER" ? (
            <PillItemSub>
              {item.orgCount
                ? `${item.orgCount} ${item.orgCount === 1 ? translate(LanguageKey.organization_key).toLowerCase() : translate(LanguageKey.organizations_key).toLowerCase()}`
                : ""}
            </PillItemSub>
          ) : (
            <PillItemSub className="d-flex align-items-center gap-1">
              <span className="text-truncate" style={{ minWidth: 0 }}>
                {item.partnerName || "Sensolus only"}
              </span>
              {item.partnerName && (
                <span className="flex-shrink-0">
                  <PartnerTypeBadge option={item} compact />
                </span>
              )}
            </PillItemSub>
          )}
        </div>
      </PillItem>
    );
  };

  const renderPillPopover = () => {
    if (!pillOpen) return null;
    return createPortal(
      <PillPanel ref={pillPopoverRef} style={{ top: pillPos.top, left: pillPos.left }}>
        <PillTabBar>
          {partnerList.length > 0 && (
            <PillTab $active={pillTab === "PARTNER"} onClick={() => handlePillTabClick("PARTNER")}>
              {translate(LanguageKey.part_key)}
            </PillTab>
          )}
          {orgList.length > 0 && (
            <PillTab $active={pillTab === "ORG"} onClick={() => handlePillTabClick("ORG")}>
              {translate(LanguageKey.org_key)}
            </PillTab>
          )}
          <PillTab $active={false} onClick={() => handlePillTabClick("ALL")}>
            {translate(LanguageKey.all_key)}
          </PillTab>
          {systemOrg && (
            <PillTab $active={false} onClick={() => handlePillTabClick("SYSTEM")}>
              {translate(LanguageKey.system_key)}
            </PillTab>
          )}
        </PillTabBar>
        <PillSearch
          ref={pillSearchRef}
          placeholder={`${translate(LanguageKey.search_key)}...`}
          value={pillSearch}
          onChange={(e) => setPillSearch(e.target.value)}
          onKeyDown={handlePillSearchKeyDown}
        />
        <PillList ref={pillListRef}>
          {pillRecentItems.length > 0 && (
            <PillRecentSection>
              <PillSectionLabel>{translate(LanguageKey.v41_recent_viewed_key)}</PillSectionLabel>
              {pillRecentItems.map((item, i) => renderPillItem(item, i, "recent-"))}
            </PillRecentSection>
          )}
          {pillRecentItems.length > 0 && <PillDivider />}
          {pillRecentItems.length > 0 && (
            <PillSectionLabel>
              {pillTab === "PARTNER"
                ? translate(LanguageKey.all_partners_key)
                : translate(LanguageKey.all_organizations_key)}
            </PillSectionLabel>
          )}
          {pillFilteredItems.map((item, i) => renderPillItem(item, pillRecentItems.length + i))}
        </PillList>
      </PillPanel>,
      document.body
    );
  };

  // --- Render mode switcher ---

  const renderModeSwitcher = () => {
    if (variant === "pill") {
      return (
        <>
          <Button
            ref={pillTriggerRef}
            variant="sensolus-greylight"
            onClick={handlePillTriggerClick}
            disabled={disabled}
            className="d-inline-flex align-items-center gap-2"
            style={{
              height: 36,
              padding: "0 12px",
              fontSize: 14,
              whiteSpace: "nowrap",
            }}
          >
            {pillIcon}
            {renderPillLabel()}
            <PillChevron $open={pillOpen} viewBox="0 0 12 12">
              <path d="M2.5 4.5L6 8L9.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </PillChevron>
          </Button>
          {!hideImpersonate && renderImpersonateButton()}
          {renderPillPopover()}
        </>
      );
    }

    // Classic ButtonGroup
    return (
      <ButtonGroup style={{ width: "100%" }}>
        {sections
          .filter((item) => item.isShow)
          .map((item, itemIdx) => (
            <SntButton
              key={itemIdx}
              style={{
                color: customStyle.adminSideBarSectionFontColor,
                borderColor: customStyle.adminSideBarSectionFontColor,
                padding: "0.375rem",
              }}
              className={`${item.id === mode ? "active" : ""}`}
              onClick={() => {
                let val = 0;
                if (item.id === "ORG") {
                  val = orgSelectedRef.current || (orgList[0] ? orgList[0].value : 0);
                } else if (item.id === "PARTNER") {
                  val = partnerSelectedRef.current || (partnerList[0] ? partnerList[0].value : 0);
                } else if (item.id === "SYSTEM" && systemOrg) {
                  val = systemOrg.value;
                }
                onChange?.({ value: val });
              }}
              icon={item.icon}
              text={item.title}
            />
          ))}
      </ButtonGroup>
    );
  };

  // --- Classic variant: formatOptionLabel for SntSelect ---

  const isSystemUser = loginInfo.organisation?.organisationType === "SYSTEM";

  const formatOptionLabel = useCallback(
    (option: OrganizationOption, { context }: { context: string }) => {
      if (context === "value") {
        if (mode === "ORG" && option.partnerName && isSystemUser) {
          return (
            <span className="d-inline-flex align-items-center gap-2">
              <span style={{ opacity: 0.6 }}>{option.partnerName}</span>
              <PartnerTypeBadge option={option} />
              <span style={{ opacity: 0.4 }}>›</span>
              <span>{option.label}</span>
            </span>
          );
        }
        if (mode === "PARTNER") {
          return (
            <span className="d-inline-flex align-items-center gap-2">
              <span>{option.label}</span>
              <PartnerTypeBadge option={option} />
            </span>
          );
        }
        return option.label;
      }
      if (!isSystemUser) return option.label;
      if (mode === "PARTNER") {
        return (
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{option.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }} className="text-truncate">
                {option.orgCount
                  ? `${option.orgCount} ${option.orgCount === 1 ? translate(LanguageKey.organization_key).toLowerCase() : translate(LanguageKey.organizations_key).toLowerCase()}`
                  : ""}
              </div>
            </div>
            <div className="flex-shrink-0 ms-2"><PartnerTypeBadge option={option} /></div>
          </div>
        );
      }
      return (
        <div className="d-flex align-items-center justify-content-between">
          <div className="text-truncate">
            <div className="text-truncate">{option.label}</div>
            <div
              style={{ fontSize: 11, opacity: 0.7 }}
              className="text-truncate d-flex align-items-center gap-1"
            >
              <span className="text-truncate" style={{ minWidth: 0 }}>
                {option.partnerName || "Sensolus only"}
              </span>
              {option.partnerName && (
                <span className="flex-shrink-0"><PartnerTypeBadge option={option} compact /></span>
              )}
            </div>
          </div>
        </div>
      );
    },
    [mode, isSystemUser]
  );

  if (variant === "pill") {
    return (
      <>
        {renderModeSwitcher()}
        <ErrorDialog />
      </>
    );
  }

  return (
    <StyleCustomInfoBtn $hoverColor={customStyle.adminSideBarSelectedColor}>
      <Row>
        <Col xs={12}>
          {renderModeSwitcher()}
        </Col>
        {(mode === "ORG" ||
            (mode === "PARTNER" && loginInfo.organisation?.organisationType !== "PARTNER")) && (
          <Col xs={12} className="mt-2">
            <div className="d-flex justify-content-between w-100">
              <div className="flex-grow-1 flex-shrink-1" style={{ minWidth: 0 }}>
                <SntSelect
                  onChange={selectChanged}
                  options={visibleItems}
                  value={allItems.filter((option) => option.value === value)}
                  isDisabled={disabled}
                  filterOption={null}
                  onInputChange={onInputChange}
                  onMenuScrollToBottom={onMenuScrollToBottom}
                  styles={{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    menu: (base: any) => ({
                      ...base,
                      ...(isSystemUser && {
                        width: mode === "PARTNER" ? "max(100%, 350px)" : "max(100%, 300px)",
                      }),
                    }),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ...(isSystemUser && { option: (base: any) => ({ ...base, padding: "6px 12px" }) }),
                  }}
                  formatOptionLabel={formatOptionLabel}
                />
              </div>
              {renderImpersonateButton()}
            </div>
          </Col>
        )}
      </Row>
      <ErrorDialog />
    </StyleCustomInfoBtn>
  );
};

export default SntOrgMenuSelectorContainer;
